import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import {
  IVideoControllerComponentsCommonProps,
  IVideoElementControllerProps,
} from 'components/videoElementController/IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import { IPlayerState } from 'types/common';
import { applyVolumeToVideoElement, removeElementFromDocument } from 'utils/common';
import { bind } from 'utils/decorators/bind';

import { CanvasVideoRenderer } from '../canvasVideoRenderer/CanvasVideoRenderer';

export class RenderController {
  private _canvasVideoRenderer: CanvasVideoRenderer | null = null;
  private _logger: TLogger;
  private _config: IVideoElementControllerProps = {};
  private _isLocalVideo = false;
  private _eventsMap = {
    [EVideoElementControllerBusEvent.SET_VOLUME]: this._setVolume,
    [EVideoElementControllerBusEvent.DESTROY]: this._destroy,
  };

  private readonly _state: StateManager<IPlayerState>;
  private readonly _eventBus: TVideoControllerEventBus;

  constructor(props: IVideoControllerComponentsCommonProps) {
    this._state = props.state;
    this._eventBus = props.eventBus;
    this._logger = Logger(this._eventBus, 'VideoElementController - RenderController');

    this._logger.info('Created');
    this._logger.debug('Initial config', this._config);
    this._logger.debug('Initial state', this._state);

    this.init();
  }

  public setConfig(config: IVideoElementControllerProps) {
    this._logger.debug('Config update received, the new one:', config);
    this._config = config;
    this.applyConfigToVideoElement();
  }

  public init() {
    this._logger.info('Initialization requested, attaching eventBus listeners');
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  @bind
  public getElements() {
    const { videoElement, canvasElement, ownContainer } = this._state.getState();

    return { videoElement, canvasElement, ownContainer };
  }

  @bind
  public getVideoElement() {
    return this.getElements().videoElement;
  }

  @bind
  public getCanvasElement() {
    return this.getElements().canvasElement;
  }

  @bind
  public renderVideoInto(container: Node) {
    const { ownContainer, videoElement } = this.getElements();

    this._logger.debug('renderVideoInto method was called');

    let hasBeenRendered = false;

    if (container && videoElement) {
      const renderTarget = ownContainer || container;

      if (videoElement.parentElement !== renderTarget) {
        hasBeenRendered = true;
        renderTarget.appendChild(videoElement);
      }

      const canvasElement = this._canvasVideoRenderer?.getCanvasElement();
      if (this._canvasVideoRenderer && canvasElement?.parentElement !== renderTarget) {
        hasBeenRendered = true;

        renderTarget.appendChild(canvasElement as HTMLCanvasElement);
        this._logger.info('Canvas element has been rendered to DOM');
      }

      if (renderTarget === ownContainer && ownContainer.parentElement !== container) {
        hasBeenRendered = true;
        container.appendChild(ownContainer);
      }

      if (hasBeenRendered) {
        this._logger.info('Video element has been rendered to DOM');
      } else {
        this._logger.verbose(
          'Skipping video element render: video element is already mounted to the DOM.',
        );
      }
    }
  }

  @bind
  public setVideoElement(videoElement: HTMLVideoElement | null, ownContainer?: HTMLDivElement) {
    this._logger.debug('Video element updated', { videoElement });
    if (videoElement === this.getVideoElement()) {
      this._logger.verbose('Received video element is equal to the previous one. Skipping');
      return;
    }

    if (this._isLocalVideo) {
      this._logger.verbose('Removing video element and container from document');
      removeElementFromDocument(this.getElements().videoElement);
      removeElementFromDocument(this.getElements().ownContainer);
    }

    this._state.setState({ videoElement, ownContainer });

    this.applyConfigToVideoElement();
  }

  @bind
  public applyConfigToVideoElement() {
    this._logger.debug('Applying config to the video element');
    if (!this.getVideoElement()) {
      this._logger.verbose('No video element known, could not apply config');
      return;
    }

    const videoElement = this.getVideoElement();

    if (!videoElement) {
      return;
    }

    const {
      isNonAnonymous,
      isMuted,
      nativeControls,
      videoElementClassName,
      canvasElementClassName,
      shouldUseCanvas,
      rootElement,
      autoplay,
    } = this._config;

    this._logger.verbose('Applied config', this._config);

    if (autoplay) {
      videoElement.autoplay = true;
    }
    videoElement.playsInline = true;

    if (nativeControls !== undefined) {
      videoElement.controls = !!nativeControls;
    }

    if (isMuted !== undefined) {
      videoElement.muted = !!isMuted;
    }

    if (isNonAnonymous !== undefined) {
      if (!isNonAnonymous) {
        videoElement.crossOrigin = 'anonymous';
      } else {
        videoElement.removeAttribute('crossOrigin');
      }
    }

    // we will remove this classname later, if canvas element will be used
    if (videoElementClassName) {
      videoElement.className = videoElementClassName;
    }

    const volume = [this._state.getState().volume, this._config.volume].find(
      (v) => v !== undefined,
    );

    if (volume !== undefined) {
      this._setVolume({ volume });
    }

    if (shouldUseCanvas && !this._canvasVideoRenderer) {
      this._logger.verbose('Adding canvas as requested');
      this._addCanvasVideoRenderer();

      if (this._state.getState().isPlaying) {
        this._canvasVideoRenderer!.startAnimationFrame();
      }
    }

    if (!shouldUseCanvas && this._canvasVideoRenderer) {
      const stream = this._state.getState().stream;

      this._logger.verbose('Removing canvas renderer');
      if (stream) {
        this._eventBus.publish(EVideoElementControllerBusEvent.UPDATE_MEDIA_STREAM, stream);
      }

      this._destroyCanvasRenderer();
    }

    const canvasElement = this.getCanvasElement();

    if (canvasElement) {
      videoElement.className = '';
      if (canvasElementClassName) {
        canvasElement.className = canvasElementClassName;
      }
    }

    // console.log('rendercontroller rootelement', rootElement);

    if (rootElement) {
      this.renderVideoInto(rootElement);
    }
  }

  public destroy() {
    this._logger.info('Destroying');
    this._destroyCanvasRenderer();

    if (this._isLocalVideo) {
      this._logger.debug('Removing video element and container from document in destroy');
      const { videoElement, ownContainer } = this.getElements();
      removeElementFromDocument(videoElement);
      removeElementFromDocument(ownContainer);
    }
  }

  @bind
  public createVideoElement() {
    this._logger.info('Creating video element');
    let videoElement;
    let ownContainer;

    if (this._config.videoElement) {
      this._logger.debug(
        'External video element passed from config - using existing one',
        this._config.videoElement,
      );
      this._isLocalVideo = false;
      videoElement = this._config.videoElement;
    } else {
      this._isLocalVideo = true;
      videoElement = document.createElement('video');
      ownContainer = document.createElement('div');
      this._logger.debug('No external video element provided in config - created the new one', {
        videoElement,
        ownContainer,
      });
      ownContainer.style.position = 'relative';
    }

    this.setVideoElement(videoElement, ownContainer);

    this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_ELEMENT_RECREATED, {
      videoElement: this.getVideoElement() as HTMLVideoElement,
    });
  }

  @bind
  private _addCanvasVideoRenderer() {
    this._logger.debug('Adding canvas renderer');
    if (!this._canvasVideoRenderer) {
      this._canvasVideoRenderer = new CanvasVideoRenderer({
        videoElement: this.getVideoElement() as HTMLVideoElement,
        className: this._config.videoElementClassName,
        state: this._state,
        eventBus: this._eventBus,
      });
    }
  }

  @bind
  private _destroyCanvasRenderer() {
    this._logger.debug('Destroying canvas renderer');
    if (this._canvasVideoRenderer) {
      this._canvasVideoRenderer.destroy();
      this._canvasVideoRenderer = null;
    }
  }

  @bind
  private _setVolume({ volume }: { volume: number }) {
    const videoElement = this.getVideoElement();

    if (videoElement) {
      applyVolumeToVideoElement(videoElement, volume);
      this._logger.verbose(`Video volume level has been set to ${volume}`);
    }
  }

  @bind
  private _destroy() {
    this._logger.info('Destroying');
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });
  }
}
